import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Button, ErrorState, LoadingState, Text, TextField } from '@cinesend/ui-kit'
import { X } from '@phosphor-icons/react'
import useStore from '../../hooks/use_store'
import EditItemModal from './edit_item_modal'

const ACTIONS_WIDTH = 'w-24 shrink-0'

const CommonValues = ({ collection, disabled, cycleID }) => {
  const [editingItem, setEditingItem] = useState(undefined)
  const [draft, setDraft] = useState('')
  const {
    data: collectionValues,
    isLoading,
    error,
    refetch,
  } = useQuery([`collection-values`, `?collectionID=${collection.id}`, `&cycleID=${cycleID}`])
  const { createModel, deleteModel } = useStore()
  const items = [...(collectionValues ?? [])].sort((a, b) =>
    (a.label ?? '').localeCompare(b.label ?? '', undefined, { sensitivity: 'base' })
  )

  const addItem = (event) => {
    event.preventDefault()
    const label = draft.trim()
    if (!label || disabled || createModel.isLoading) return
    createModel.mutate(
      {
        endpoint: 'collection-values',
        data: { collection_id: collection.id, label },
      },
      {
        onSuccess: () => setDraft(''),
      }
    )
  }

  if (error) {
    return (
      <ErrorState message={error.message ?? 'An error occurred. Please contact customer support.'} onRetry={refetch} />
    )
  }

  if (isLoading) {
    return <LoadingState className='justify-center' />
  }

  return (
    <>
      <div className='-mx-2.5 flex items-center gap-2 pl-4 pr-2 pb-1 pt-2'>
        <Text as='span' variant='xs-medium' tone='secondary' className='min-w-0 flex-1'>
          Value
        </Text>
        <div className={ACTIONS_WIDTH} aria-hidden='true' />
      </div>
      {items.length === 0 ? (
        <Text as='p' variant='sm' tone='secondary' className='px-1.5 py-2'>
          No values yet.
        </Text>
      ) : (
        <ul className='m-0 -mx-2.5 flex list-none flex-col gap-1 p-0'>
          {items.map((item) => (
            <li key={item.id}>
              <div
                role={disabled ? undefined : 'button'}
                tabIndex={disabled ? undefined : 0}
                className={`flex items-center gap-2 rounded-lg py-2 pl-4 pr-2 ${
                  disabled
                    ? ''
                    : 'cursor-pointer hover:bg-[var(--cs-color-background-tertiary)] focus-visible:outline-none focus-visible:shadow-[var(--cs-focus-ring-default-sm)]'
                }`}
                onClick={disabled ? undefined : () => setEditingItem(item)}
                onKeyDown={
                  disabled
                    ? undefined
                    : (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          setEditingItem(item)
                        }
                      }
                }
              >
                <Text as='span' variant='sm-medium' className='min-w-0 flex-1 truncate'>
                  {item.label || '—'}
                </Text>
                <div className={`flex ${ACTIONS_WIDTH} justify-end`}>
                  <Button
                    variant='ghost'
                    iconOnly
                    leadingIcon={<X size={20} weight='regular' aria-hidden='true' />}
                    onClick={(event) => {
                      event.stopPropagation()
                      deleteModel.mutate({
                        endpoint: 'collection-values',
                        id: item.id,
                        params: `?cycleID=${cycleID}`,
                        deleteNoun: 'value',
                        deleteName: item.label,
                      })
                    }}
                    disabled={disabled || item.hidden_at}
                  >
                    Remove item
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      {!disabled ? (
        <form className='-mx-2.5 flex items-center gap-2 pl-4 pr-2' onSubmit={addItem}>
          <div className='min-w-0 flex-1'>
            <TextField
              label='Value'
              hideLabel
              placeholder='Add value'
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
          </div>
          <div className={`flex ${ACTIONS_WIDTH} justify-end`}>
            <Button type='submit' disabled={!draft.trim() || createModel.isLoading}>
              Add
            </Button>
          </div>
        </form>
      ) : null}
      {editingItem !== undefined ? (
        <EditItemModal collection={collection} item={editingItem} onClose={() => setEditingItem(undefined)} />
      ) : null}
    </>
  )
}

export default CommonValues
